/**
 * Utility functions for Shopify checkout
 */

/**
 * Adds necessary authentication parameters to Shopify checkout URL
 * Ensures the URL uses the correct Shopify domain from environment variables
 * @param {string} checkoutUrl - Original checkout URL from Shopify
 * @returns {string} - Enhanced checkout URL with auth parameters
 */
export const enhanceCheckoutUrl = (checkoutUrl) => {
  if (!checkoutUrl) return '';
  
  try {
    const url = new URL(checkoutUrl);
    
    // Get the token from environment variables
    const token = process.env.REACT_APP_SHOPIFY_STOREFRONT_API_TOKEN;
    
    // Check if token is missing
    if (!token) {
      console.error('Missing Shopify Storefront API token. Please check your environment variables.');
    }
    
    // Log the checkout URL for debugging
    console.log(`Enhancing checkout URL: ${checkoutUrl}`);
    
    // IMPORTANT: If this is a shop.app URL, redirect to sprezzatura-dev.myshopify.com
    // This is critical for authentication to work properly
    if (url.hostname.includes('shop.app')) {
      console.log('Detected shop.app URL - redirecting to sprezzatura-dev.myshopify.com');
      
      // Extract checkout ID if possible
      try {
        const pathParts = url.pathname.split('/');
        const checkoutId = pathParts[2]; // Extract checkout ID
        
        if (checkoutId && !isNaN(checkoutId)) {
          // Redirect to Shopify checkout directly
          const redirectUrl = `https://sprezzatura-dev.myshopify.com/checkout/${checkoutId}`;
          console.warn(`Redirecting shop.app URL to: ${redirectUrl}`);
          return redirectUrl;
        }
      } catch (e) {
        console.error('Error parsing shop.app URL:', e);
      }
    }
    
    // For regular checkout URLs, use the correct Shopify domain
    const shopifyDomain = 'sprezzatura-dev.myshopify.com';
    
    // Format domain properly
    const formattedDomain = shopifyDomain.replace(/^https?:\/\//, '').replace(/\/$/, '');
    
    // Ensure URL uses the correct domain for non-private_access_tokens URLs
    if ((url.hostname.includes('myshopify.com') || url.hostname.includes('shop.app')) && 
        url.hostname !== formattedDomain) {
      url.hostname = formattedDomain;
    }
    
    // Add origin as a parameter to help with cross-domain authentication
    url.searchParams.append('checkout_site_origin', window.location.origin);
    
    // Add timestamp to prevent caching issues
    url.searchParams.append('_t', Date.now());
    
    // Add token in URL for authorization
    if (!url.searchParams.has('access_token')) {
      url.searchParams.append('access_token', token);
    }
    
    console.log(`Enhanced checkout URL: ${url.toString()}`);
    return url.toString();
  } catch (error) {
    console.error('Error enhancing checkout URL:', error);
    return checkoutUrl;
  }
};
